/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-cohorts-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';

import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { useGridCells, useTranslation } from 'hooks';
import { StudyCohortFields } from 'queries/generated/fragments.generated';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import { GridColumnDateFilterV2 } from '@components/core/grid-filters-v2';

const AdminStudyCohortsGrid = ({
    cohortsData,
    cohorts,
    openAddCohort,
    setCohort,
    setCurrentCohort,
    canEdit,
    isSysAdmin,
}: {
    cohortsData: StudyCohortFields[];
    cohorts: StudyCohortFields[];
    openAddCohort: any;
    setCohort: Function;
    setCurrentCohort: Function;
    canEdit: boolean;
    isSysAdmin: boolean;
}) => {
    const { t } = useTranslation();
    const { YesNoCell, NumberCell, DateCell, MaxLengthCell } = useGridCells();

    useEffect(() => {
        setCohort(cohortsData);
        let array = [...cohortsData];
        array = restructuredUpdatedCreatedBy(array);
        array = orderBy(array, [{ field: 'name', dir: 'asc' }]) ?? [];
        setCohort(array);
    }, [cohortsData]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addCohort'),
            onClick: () => openAddCohort.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            disabled: !canEdit,
            icon: ['fal', 'plus'],
        },
    ];

    return (
        <ExpandableGridV2
            className="svt-grid-vertical-scroll"
            title={t('admin:COHORTS')}
            id="cohortsGrid"
            key="cohortsGrid"
            customKey="cohorts"
            resizable
            showPager
            reorderable
            hideCheckbox
            hideExpand
            scrollable="scrollable"
            groupable
            pageable={false}
            items={cohorts}
            columns={[
                {
                    label: t('admin:sourceSystemId'),
                    id: 'cohort-code',
                    field: 'subjectCode',
                    minWidth: 200,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(30) },
                },
                {
                    label: t('common:name'),
                    id: 'cohort-name',
                    field: 'name',
                    minWidth: 160,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(60) },
                },
                {
                    label: t('common:description'),
                    field: 'description',
                    id: 'cohort-description',
                    minWidth: 190,
                    cells: { data: MaxLengthCell(150) },
                },
                {
                    label: t('common:active'),
                    field: 'active',
                    id: 'cohort-active',
                    minWidth: 160,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:createdBy'),
                    id: 'cohort-createdby',
                    field: '_createdByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    id: 'cohort-created',
                    minWidth: 200,
                    field: 'created',
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilterV2,
                },
                {
                    label: t('ra:updatedBy'),
                    id: 'cohort-updatedby',
                    field: '_updatedByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'cohort-updated',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilterV2,
                },
                {
                    field: 'recordId',
                    label: t('id'),
                    minWidth: 150,
                    id: 'cohort-id',
                    filter: 'numeric',
                    cells: { data: NumberCell },
                    showByDefault: false,
                },
            ]}
            rowActions={(cohort: StudyCohortFields) => [
                {
                    label: 'admin:editCohort',
                    icon: ['fal', 'edit'],
                    onClick: () => {
                        setCurrentCohort(cohort);
                        openAddCohort.setTrue();
                    },
                    enabled: canEdit || isSysAdmin,
                },
            ]}
            extraMenuItems={extraMenuItems}
        />
    );
};

export default AdminStudyCohortsGrid;
